import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Sparkles } from "lucide-react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  getGetDoclistQueryKey,
  useCreateChat,
  useGetDoclist,
} from "~/api/chat/chat";
import { Folder, FolderChatStatus } from "~/api/model";
import { getGetProjectQueryKey, useGetProject } from "~/api/projects/projects";
import { FancyButton } from "~/components/fancy-button";
import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import { useTermsStore } from "~/features/chat/stores/termsStore";
import { Hint } from "~/features/chat/types";
import { ROUTES, useProjectParams } from "~/helpers";
import { useWebsocket } from "~/hooks/useWebsocket";
import { useAuthStore } from "~/stores/authStore";
import { formatProcessingEstimate } from "~/utils/date";
import ConditionalTooltip from "./ConditionalTooltip";

type NewChatButtonProps = {
  documentIds: string[];
  disabled?: boolean;
  totalPages?: number;
  mobileLabel?: string;
  useMobileStyle?: boolean;
};

export const NewChatButton: FC<NewChatButtonProps> = ({
  documentIds,
  disabled = false,
  totalPages = 0,
  mobileLabel,
  useMobileStyle = false,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { data: currentCase, refetch: refetchProject } = useGetProject(
    organizationId,
    caseId
  );
  const { mutateAsync: createChat } = useCreateChat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isChatPending, setIsChatPending] = useState(false);
  const intl = useIntl();
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const processingStartTimeRef = useRef<number | null>(null);
  const isAuthExpired = useAuthStore((state) => state.isAuthExpired);

  const { addListener, removeListener } = useWebsocket<{ folder: Folder }>({
    projectId: caseId,
    subscribeTo: "folder-update-log",
  });

  const { addListener: addTermListener, removeListener: removeTermListener } =
    useWebsocket<{ term: Hint }>({
      projectId: caseId,
      subscribeTo: "term-definition-log",
    });
  const addTerms = useTermsStore((s) => s.addTerms);

  const isCaseReady = currentCase?.chatStatus === FolderChatStatus.COMPLETE;
  const { isError } = useGetDoclist(organizationId, caseId, undefined, {
    query: {
      enabled: isCaseReady && documentIds.length > 0,
    },
  });

  const handleClick = async () => {
    setIsChatPending(true);
    const chat = await createChat({
      organizationId,
      projectId: caseId,
      data: {
        documentIds,
        title: currentCase?.name ?? "Untitled Chat",
      },
    });

    setIsChatPending(false);

    navigate(
      ROUTES.CHAT_DOCUMENT({
        organizationId,
        caseId,
        chatId: chat.id,
      })
    );
  };

  const tooltipMessage = useMemo(() => {
    if (isAuthExpired) {
      return (
        <FormattedMessage
          id="documentsList.sessionExpired"
          defaultMessage="Session expired. Please sign in again."
        />
      );
    }

    if (isError) {
      return (
        <FormattedMessage
          id="documentsList.docListError"
          defaultMessage="Sky is unavailable. Please contact support team."
        />
      );
    }

    return undefined;
  }, [isAuthExpired, isError]);

  useEffect(() => {
    const handler = (updates: { folder: Folder }[]) => {
      const folder = { ...updates[0].folder };
      const key = getGetProjectQueryKey(organizationId, caseId);
      let shouldInvalidate = false;
      let shouldInvalidateDoclist = false;

      queryClient.setQueryData<Folder | undefined>(key, (old) => {
        if (!old) {
          shouldInvalidate = true;
          return old;
        }

        if (
          folder.chatStatus === FolderChatStatus.COMPLETE ||
          folder.chatStatus === FolderChatStatus.ERROR
        ) {
          shouldInvalidate = true;
          if (folder.chatStatus === FolderChatStatus.COMPLETE) {
            shouldInvalidateDoclist = true;
          }
        }

        return folder;
      });

      if (shouldInvalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }

      if (shouldInvalidateDoclist) {
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, caseId),
        });
      }
    };
    addListener(handler);
    return () => removeListener(handler);
  }, [addListener, removeListener, organizationId, caseId, queryClient]);

  useEffect(() => {
    const isProcessing =
      currentCase?.chatStatus === FolderChatStatus.PROCESSING ||
      currentCase?.chatStatus === FolderChatStatus.COPYING;

    if (!isProcessing || isAuthExpired) {
      return;
    }

    const pollInterval = setInterval(() => {
      refetchProject();
    }, 15000);

    return () => clearInterval(pollInterval);
  }, [currentCase?.chatStatus, refetchProject, isAuthExpired]);

  useEffect(() => {
    const handler = (updates: { term: Hint }[]) => {
      const incoming = updates
        .map((u) => u.term)
        .filter((t): t is Hint => !!t?.term);
      if (incoming.length) {
        addTerms(
          caseId,
          incoming.filter(
            (t) => !["#", "term", "data"].includes(t.term.toLowerCase())
          )
        );
      }
    };
    addTermListener(handler);
    return () => removeTermListener(handler);
  }, [addTermListener, removeTermListener, addTerms, caseId]);

  const isButtonDisabled =
    disabled ||
    isAuthExpired ||
    isError ||
    currentCase?.chatStatus !== FolderChatStatus.COMPLETE ||
    isChatPending;

  const buttonClassName = useMemo(() => {
    const isErrorState =
      isAuthExpired ||
      isError ||
      currentCase?.chatStatus === FolderChatStatus.ERROR;

    return clsx("gap-2 text-white", {
      "bg-red-600 hover:bg-red-700": isErrorState,
      "bg-blue-600 hover:bg-blue-700 active:scale-95 transition-all duration-300":
        !isErrorState,
    });
  }, [isAuthExpired, isError, currentCase?.chatStatus]);

  const showProcessing =
    (disabled ||
      currentCase?.chatStatus === FolderChatStatus.PROCESSING ||
      currentCase?.chatStatus === FolderChatStatus.COPYING) &&
    currentCase?.chatStatus !== FolderChatStatus.ERROR;

  useEffect(() => {
    if (showProcessing && totalPages > 0) {
      if (processingStartTimeRef.current === null) {
        processingStartTimeRef.current = Date.now();
        const estimateSeconds = Math.ceil((totalPages / 25) * 60);
        setRemainingTime(estimateSeconds);
      }
    } else {
      processingStartTimeRef.current = null;
      setRemainingTime(null);
    }
  }, [showProcessing, totalPages]);

  useEffect(() => {
    if (!showProcessing || remainingTime === null) {
      return;
    }

    const interval = setInterval(() => {
      if (processingStartTimeRef.current !== null) {
        const elapsed = Math.floor(
          (Date.now() - processingStartTimeRef.current) / 1000
        );
        const initialEstimate = Math.ceil((totalPages / 25) * 60);
        const newRemaining = Math.max(0, initialEstimate - elapsed);
        setRemainingTime(newRemaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showProcessing, remainingTime, totalPages]);

  if (documentIds.length === 0) {
    return null;
  }

  return (
    <ConditionalTooltip message={tooltipMessage}>
      <div className="group">
        {currentCase?.chatStatus === FolderChatStatus.COMPLETE &&
          (useMobileStyle ? (
            <Button
              data-tour="launch-sky"
              disabled={isButtonDisabled}
              onClick={handleClick}
              variant="ghost"
              className="text-blue-600 hover:text-blue-700"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              {mobileLabel ??
                intl.formatMessage({
                  id: "documentsList.launchSky",
                  defaultMessage: "Launch Sky",
                })}
            </Button>
          ) : (
            <div
              data-tour="launch-sky"
              onClick={isButtonDisabled ? undefined : handleClick}
              style={{
                cursor: isButtonDisabled ? "not-allowed" : "pointer",
                opacity: isButtonDisabled ? 0.6 : 1,
              }}
            >
              <FancyButton
                title={
                  mobileLabel ??
                  intl.formatMessage({
                    id: "documentsList.launchSky",
                    defaultMessage: "Launch Sky",
                  })
                }
              />
            </div>
          ))}
        {currentCase?.chatStatus !== FolderChatStatus.COMPLETE && (
          <Button
            disabled={isButtonDisabled}
            onClick={handleClick}
            className={buttonClassName}
          >
            {isAuthExpired ? (
              <FormattedMessage
                id="documentsList.sessionExpired"
                defaultMessage="Session expired. Please sign in again."
              />
            ) : showProcessing ? (
              <>
                <Loader
                  isShowPng={true}
                  isWhite={true}
                  className="h-4 w-4 animate-spin"
                />
                {totalPages > 0 && remainingTime !== null ? (
                  remainingTime > 0 ? (
                    <FormattedMessage
                      id="documentsList.etaRemaining"
                      defaultMessage="{estimate} remaining"
                      values={{
                        estimate: formatProcessingEstimate(
                          Math.ceil(remainingTime / 60) * 25,
                          intl.locale
                        ),
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="documentsList.soon"
                      defaultMessage="Soon"
                    />
                  )
                ) : (
                  <FormattedMessage
                    id="documentsList.preparingChat"
                    defaultMessage="Preparing..."
                  />
                )}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 transition-transform duration-300 group-hover:scale-125" />
                <FormattedMessage
                  id="documentsList.launchSky"
                  defaultMessage="Launch Sky"
                />
              </>
            )}
          </Button>
        )}
      </div>
    </ConditionalTooltip>
  );
};
